import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { HeartButton } from "@/components/HeartButton";
import { SERVICES, IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const Group = ({ group, open, onToggle, testid }) => (
  <div className="border-b border-kiki-line">
    <button data-testid={`${testid}-toggle`} onClick={onToggle} className="w-full flex items-center justify-between gap-4 py-5 text-left group">
      <span>
        <span className="block font-display text-2xl font-medium text-kiki-ink">{group.name}</span>
        <span className="block text-xs font-mono uppercase tracking-widest text-kiki-muted mt-1">{group.items.length} services</span>
      </span>
      <span className={`shrink-0 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-500 ${
        open ? "bg-kiki-gold border-kiki-gold text-[#FAF5EC] rotate-90" : "border-kiki-line text-kiki-ink group-hover:border-kiki-gold group-hover:text-kiki-gold"
      }`}>
        <ChevronRight className="w-4 h-4" />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div data-testid={`${testid}-items`} className="pb-6 space-y-3">
            {group.items.map((item, i) => (
              <div key={item.n} data-testid={`${testid}-item-${i}`} className="rounded-xl border border-kiki-line bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-outfit font-medium text-kiki-ink">{item.n}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-outfit font-semibold text-kiki-gold">{item.p}</p>
                    <HeartButton
                      name={item.n}
                      price={item.p}
                      testid={`item-heart-${item.n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    />
                  </div>
                </div>
                <p className="text-sm text-kiki-muted font-light leading-relaxed mt-1.5">{item.s}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const svc = SERVICES.find((s) => s.slug === slug);
  const [openGroup, setOpenGroup] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpenGroup(0);
  }, [slug]);

  if (!svc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF5EC] text-kiki-ink">
        <p className="font-display text-4xl font-medium">Service not found</p>
        <Link data-testid="service-notfound-home-link" to="/" className="mt-6 text-kiki-gold font-outfit flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Kiki Services
        </Link>
      </div>
    );
  }

  const total = svc.groups.reduce((a, g) => a + g.items.length, 0);
  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollToId("contact"), 500);
  };

  return (
    <div data-testid="service-page" className="bg-[#FAF5EC] min-h-screen text-kiki-ink warm-spotlight">
      <header className="border-b border-kiki-line bg-[#FAF5EC]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link data-testid="service-back-link" to="/" className="flex items-center gap-2 text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display text-xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></span>
          </Link>
          <button data-testid="service-header-cta" onClick={goContact} className="flex items-center gap-1.5 bg-kiki-ink hover:bg-kiki-gold text-[#FAF5EC] text-sm font-outfit font-semibold rounded-full px-5 py-2.5 transition-colors duration-300">
            Start a project <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>{svc.tag} · Service {svc.id} of 12</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 data-testid="service-title" className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-kiki-ink mt-3 leading-[1.05]">{svc.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-lg text-kiki-muted font-light leading-relaxed max-w-2xl">{svc.line}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-4 text-sm text-kiki-muted font-light max-w-2xl">
              {total} services across {svc.groups.length} areas. Available individually, as projects, or through
              recurring support. Pricing is indicative and varies with scope, volume and complexity.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 border border-kiki-gold/25 rounded-2xl" />
            <div className="relative overflow-hidden rounded-xl aspect-[4/3]" data-testid="service-hero-image">
              <img src={IMAGES[svc.image]} alt={svc.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <Reveal><Eyebrow>Full catalogue — tap a heading to expand</Eyebrow></Reveal>
        <div className="border-t border-kiki-line mt-4">
          {svc.groups.map((g, i) => (
            <Group
              key={g.name}
              group={g}
              testid={`service-group-${i}`}
              open={openGroup === i}
              onToggle={() => setOpenGroup(openGroup === i ? null : i)}
            />
          ))}
        </div>
      </section>

      <section className="border-t border-kiki-line bg-kiki-surface/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-medium text-kiki-ink">
              Discuss <span className="italic text-kiki-gold">{svc.title.toLowerCase()}</span> for your business
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <button data-testid="service-bottom-cta" onClick={goContact} className="mt-8 inline-flex items-center gap-2 bg-kiki-ink hover:bg-kiki-gold text-[#FAF5EC] font-outfit font-semibold rounded-full px-8 py-4 transition-colors duration-300">
              Book a consultation <ArrowUpRight className="w-4 h-4" />
            </button>
          </Reveal>
          <p className="mt-14 font-mono text-[10px] uppercase tracking-widest text-kiki-muted">© 2026 Kiki Services · kikiservices.co.in</p>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
